// §11.9 — the calculator's arithmetic, kept apart from its chrome so it can be tested as maths
// rather than as a UI.

export type Operator = '+' | '−' | '×' | '÷' | 'AND' | 'OR' | 'XOR' | 'LSH' | 'RSH' | 'MOD';
export type Base = 'hex' | 'dec' | 'oct' | 'bin';
/** §34.3 — `convert` is the third mode, beside standard and programmer. */
export type Mode = 'standard' | 'programmer' | 'convert';

export const BASE_RADIX: Record<Base, number> = { hex: 16, dec: 10, oct: 8, bin: 2 };
export const BASE_DIGITS: Record<Base, string> = {
  hex: '0123456789ABCDEF',
  dec: '0123456789',
  oct: '01234567',
  bin: '01',
};

export interface HistoryEntry {
  expression: string;
  result: string;
}

export interface CalculatorState {
  mode: Mode;
  base: Base;
  /** What the display shows, as typed. */
  entry: string;
  /** The left-hand operand of a pending operation, or `null`. */
  accumulator: number | null;
  pending: Operator | null;
  /** True while `entry` still shows a result rather than something the user typed. */
  replacing: boolean;
  history: HistoryEntry[];
}

export function initialState(): CalculatorState {
  return {
    mode: 'standard',
    base: 'dec',
    entry: '0',
    accumulator: null,
    pending: null,
    replacing: true,
    history: [],
  };
}

/** Bit-width of the programmer mode's integer operations. */
const PROGRAMMER_BITS = 32;

export function parseEntry(entry: string, base: Base): number {
  if (base === 'dec') {
    const value = Number(entry);
    return Number.isFinite(value) ? value : 0;
  }
  const value = Number.parseInt(entry || '0', BASE_RADIX[base]);
  return Number.isFinite(value) ? value : 0;
}

export function formatValue(value: number, base: Base, mode: Mode): string {
  if (mode === 'programmer' || base !== 'dec') {
    const truncated = Math.trunc(value);
    const unsigned = truncated < 0 ? truncated >>> 0 : truncated;
    return unsigned.toString(BASE_RADIX[base]).toUpperCase();
  }
  if (!Number.isFinite(value)) return 'Error';
  // Trim floating-point noise without turning integers into `1.000000000`.
  const rounded = Number.parseFloat(value.toPrecision(12));
  return String(rounded);
}

function toInt32(value: number): number {
  return Math.trunc(value) | 0;
}

export function applyOperator(a: number, operator: Operator, b: number): number {
  switch (operator) {
    case '+':
      return a + b;
    case '−':
      return a - b;
    case '×':
      return a * b;
    case '÷':
      return b === 0 ? Number.NaN : a / b;
    case 'MOD':
      return b === 0 ? Number.NaN : a % b;
    case 'AND':
      return toInt32(a) & toInt32(b);
    case 'OR':
      return toInt32(a) | toInt32(b);
    case 'XOR':
      return toInt32(a) ^ toInt32(b);
    case 'LSH':
      return toInt32(a) << (toInt32(b) % PROGRAMMER_BITS);
    case 'RSH':
      return toInt32(a) >> (toInt32(b) % PROGRAMMER_BITS);
  }
}

export function pressDigit(state: CalculatorState, digit: string): CalculatorState {
  const allowed = BASE_DIGITS[state.base];
  const upper = digit.toUpperCase();
  if (!allowed.includes(upper)) return state;

  if (state.replacing || state.entry === '0') return { ...state, entry: upper, replacing: false };
  return { ...state, entry: state.entry + upper, replacing: false };
}

export function pressDecimalPoint(state: CalculatorState): CalculatorState {
  if (state.mode === 'programmer' || state.base !== 'dec') return state;
  if (state.replacing) return { ...state, entry: '0.', replacing: false };
  return state.entry.includes('.') ? state : { ...state, entry: `${state.entry}.`, replacing: false };
}

export function pressBackspace(state: CalculatorState): CalculatorState {
  if (state.replacing) return state;
  const next = state.entry.slice(0, -1);
  return { ...state, entry: next.length === 0 ? '0' : next };
}

export function pressClear(state: CalculatorState): CalculatorState {
  return { ...state, entry: '0', accumulator: null, pending: null, replacing: true };
}

export function pressNegate(state: CalculatorState): CalculatorState {
  const value = parseEntry(state.entry, state.base);
  return { ...state, entry: formatValue(-value, state.base, state.mode), replacing: false };
}

export function pressOperator(state: CalculatorState, operator: Operator): CalculatorState {
  const value = parseEntry(state.entry, state.base);
  if (state.pending !== null && state.accumulator !== null && !state.replacing) {
    const result = applyOperator(state.accumulator, state.pending, value);
    return {
      ...state,
      entry: formatValue(result, state.base, state.mode),
      accumulator: result,
      pending: operator,
      replacing: true,
    };
  }
  return { ...state, accumulator: value, pending: operator, replacing: true };
}

export function pressEquals(state: CalculatorState): CalculatorState {
  if (state.pending === null || state.accumulator === null) return state;

  const right = parseEntry(state.entry, state.base);
  const result = applyOperator(state.accumulator, state.pending, right);
  const expression = `${formatValue(state.accumulator, state.base, state.mode)} ${state.pending} ${formatValue(right, state.base, state.mode)}`;
  const formatted = Number.isNaN(result) ? 'Error' : formatValue(result, state.base, state.mode);

  return {
    ...state,
    entry: formatted,
    accumulator: null,
    pending: null,
    replacing: true,
    history: [{ expression, result: formatted }, ...state.history].slice(0, 50),
  };
}

export function setMode(state: CalculatorState, mode: Mode): CalculatorState {
  if (mode === state.mode) return state;
  const value = parseEntry(state.entry, state.base);
  // Only the programmer keeps a base: a converted quantity is a decimal number, so leaving `hex`
  // set on the way out would show 25.4 as 19 (§34.3).
  const base: Base = mode === 'programmer' ? state.base : 'dec';
  return { ...state, mode, base, entry: formatValue(value, base, mode), replacing: true };
}

export function setBase(state: CalculatorState, base: Base): CalculatorState {
  if (base === state.base) return state;
  const value = parseEntry(state.entry, state.base);
  return { ...state, base, entry: formatValue(value, base, state.mode), replacing: true };
}

/** Every base's rendering of the current entry, for the programmer readout. */
export function baseReadout(state: CalculatorState): Record<Base, string> {
  const value = parseEntry(state.entry, state.base);
  return {
    hex: formatValue(value, 'hex', 'programmer'),
    dec: formatValue(value, 'dec', 'programmer'),
    oct: formatValue(value, 'oct', 'programmer'),
    bin: formatValue(value, 'bin', 'programmer'),
  };
}

// ── §34.3 (D-501) — the converter's third mode: eight categories, pinned unit tables ─────────
//
// **The unit list is the spec's, exactly**, and adding a ninth category or a tenth length unit is a
// spec change rather than an improvement (D-501). No currency, ever (D-490) — it would need egress
// forever, and this app has none.
//
// Every unit is its size in its category's **base unit, as an exact fraction of integers**, and
// that is the one interesting decision in here. §34.3 asks for factors that are exact where exact
// factors exist — an inch is 25.4 mm *by definition*, a nautical mile is 1852 m, a pound is
// 453.59237 g — and a table of doubles cannot deliver that: `0.0254 / 0.001` is not 25.4 in binary
// floating point, it is 25.400000000000002, and a converter whose headline case is off by 2e-15 is
// wrong in the way that makes people stop trusting the whole app. Integers over integers, reduced
// before they are divided, give the answer a person would write down.

export type ConvertCategory =
  | 'length'
  | 'mass'
  | 'temperature'
  | 'speed'
  | 'area'
  | 'volume'
  | 'data'
  | 'time';

/** A unit's size in its category's base unit, as `num / den`. */
interface UnitSize {
  readonly num: number;
  readonly den: number;
}

export interface ConvertUnit extends UnitSize {
  /** What the dropdown shows, and what the tests name. */
  readonly label: string;
}

/** US liquid gallon, exactly 3.785411784 L — every other US volume unit is a division of it. */
const GAL_NUM = 3_785_411_784;
const GAL_DEN = 1_000_000_000;
const usVolume = (per: number): UnitSize => ({ num: GAL_NUM, den: GAL_DEN * per });

/**
 * The eight tables, in §34.3's order, with §34.3's units and no others.
 *
 * The **first** unit of each category is its base, which is also the identity row — a table whose
 * base is not 1/1 would convert everything through a rounding step for no reason.
 */
export const CONVERT_UNITS: Record<ConvertCategory, readonly ConvertUnit[]> = {
  // base: metre
  length: [
    { label: 'm', num: 1, den: 1 },
    { label: 'mm', num: 1, den: 1000 },
    { label: 'cm', num: 1, den: 100 },
    { label: 'km', num: 1000, den: 1 },
    // Exact by definition: 1 in = 25.4 mm, and the imperial units below are all multiples of it.
    { label: 'in', num: 254, den: 10_000 },
    { label: 'ft', num: 3048, den: 10_000 },
    { label: 'yd', num: 9144, den: 10_000 },
    { label: 'mi', num: 1_609_344, den: 1000 },
    // The **nautical** mile, 1852 m exactly — not 6076 ft, and not the statute mile above it.
    { label: 'nm', num: 1852, den: 1 },
  ],
  // base: gram
  mass: [
    { label: 'g', num: 1, den: 1 },
    { label: 'mg', num: 1, den: 1000 },
    { label: 'kg', num: 1000, den: 1 },
    { label: 't', num: 1_000_000, den: 1 },
    // 1 lb = 453.59237 g exactly; 1 oz is a sixteenth of it, so it is written as one.
    { label: 'oz', num: 45_359_237, den: 16 * 100_000 },
    { label: 'lb', num: 45_359_237, den: 100_000 },
  ],
  // Affine, not scalar — handled by `convert` itself. The rows exist for the dropdowns' order.
  temperature: [
    { label: '°C', num: 1, den: 1 },
    { label: '°F', num: 1, den: 1 },
    { label: 'K', num: 1, den: 1 },
  ],
  // base: metre per second
  speed: [
    { label: 'm/s', num: 1, den: 1 },
    { label: 'km/h', num: 1000, den: 3600 },
    { label: 'mph', num: 1_609_344, den: 1000 * 3600 },
    // The knot: one nautical mile per hour, which is what makes 1 kn = 1.852 km/h exact.
    { label: 'kn', num: 1852, den: 3600 },
    { label: 'ft/min', num: 3048, den: 10_000 * 60 },
  ],
  // base: square metre
  area: [
    { label: 'm²', num: 1, den: 1 },
    { label: 'ft²', num: 3048 * 3048, den: 10_000 * 10_000 },
    // An acre is 43 560 ft² exactly, so it is written from the foot rather than as 4046.8564224.
    { label: 'acre', num: 43_560 * 3048 * 3048, den: 10_000 * 10_000 },
    { label: 'ha', num: 10_000, den: 1 },
    { label: 'km²', num: 1_000_000, den: 1 },
    { label: 'mi²', num: 1_609_344 * 1_609_344, den: 1000 * 1000 },
  ],
  // base: litre
  volume: [
    { label: 'L', num: 1, den: 1 },
    { label: 'mL', num: 1, den: 1000 },
    { label: 'm³', num: 1000, den: 1 },
    { label: 'fl-oz', ...usVolume(128) },
    { label: 'cup', ...usVolume(16) },
    { label: 'pt', ...usVolume(8) },
    { label: 'qt', ...usVolume(4) },
    { label: 'gal', ...usVolume(1) },
    // 1 in³ = 16.387064 mL exactly (2.54³ cm³), and a cubic foot is 1728 of them.
    { label: 'in³', num: 16_387_064, den: 1_000_000_000 },
    { label: 'ft³', num: 1728 * 16_387_064, den: 1_000_000_000 },
  ],
  // base: bit. **Decimal and binary prefixes are both listed and they are not the same** — that is
  // the whole reason a converter has this category at all.
  data: [
    { label: 'bit', num: 1, den: 1 },
    { label: 'B', num: 8, den: 1 },
    { label: 'KB', num: 8 * 1000, den: 1 },
    { label: 'MB', num: 8 * 1000 ** 2, den: 1 },
    { label: 'GB', num: 8 * 1000 ** 3, den: 1 },
    { label: 'TB', num: 8 * 1000 ** 4, den: 1 },
    { label: 'KiB', num: 8 * 1024, den: 1 },
    { label: 'MiB', num: 8 * 1024 ** 2, den: 1 },
    { label: 'GiB', num: 8 * 1024 ** 3, den: 1 },
    { label: 'TiB', num: 8 * 1024 ** 4, den: 1 },
  ],
  // base: millisecond
  time: [
    { label: 'ms', num: 1, den: 1 },
    { label: 's', num: 1000, den: 1 },
    { label: 'min', num: 60_000, den: 1 },
    { label: 'h', num: 3_600_000, den: 1 },
    { label: 'day', num: 86_400_000, den: 1 },
    { label: 'week', num: 604_800_000, den: 1 },
  ],
};

export const CONVERT_CATEGORIES = Object.keys(CONVERT_UNITS) as ConvertCategory[];

/** What the mode's category tabs are called. */
export const CATEGORY_LABELS: Record<ConvertCategory, string> = {
  length: 'Length',
  mass: 'Mass',
  temperature: 'Temperature',
  speed: 'Speed',
  area: 'Area',
  volume: 'Volume',
  data: 'Data',
  time: 'Time',
};

export function unitsOf(category: ConvertCategory): readonly ConvertUnit[] {
  return CONVERT_UNITS[category];
}

function unitBy(category: ConvertCategory, label: string): ConvertUnit | undefined {
  return CONVERT_UNITS[category].find((unit) => unit.label === label);
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x === 0n ? 1n : x;
}

/**
 * Temperature, the one category that is **affine rather than scalar**: it has an offset, so a
 * ratio is meaningless (20 °C is not twice 10 °C). Everything routes through Celsius.
 */
function toCelsius(value: number, from: string): number {
  if (from === '°F') return ((value - 32) * 5) / 9;
  if (from === 'K') return value - 273.15;
  return value;
}

function fromCelsius(celsius: number, to: string): number {
  if (to === '°F') return (celsius * 9) / 5 + 32;
  if (to === 'K') return celsius + 273.15;
  return celsius;
}

/**
 * Convert `value` from one unit to another inside a category. Returns `null` when either unit does
 * not belong to it — the two dropdowns are driven from the same table, so that is a programming
 * error rather than something a user can do.
 *
 * The scalar path multiplies and divides in `BigInt` and reduces **before** the single division that
 * produces the answer, so exactness survives units whose numerators are billions (the US volume
 * table) and whose products would leave `Number.MAX_SAFE_INTEGER` far behind.
 */
export function convert(
  value: number,
  category: ConvertCategory,
  fromLabel: string,
  toLabel: string,
): number | null {
  const from = unitBy(category, fromLabel);
  const to = unitBy(category, toLabel);
  if (!from || !to) return null;
  if (category === 'temperature') return fromCelsius(toCelsius(value, fromLabel), toLabel);
  if (!Number.isFinite(value)) return Number.NaN;

  let num = BigInt(from.num) * BigInt(to.den);
  let den = BigInt(from.den) * BigInt(to.num);
  const common = gcd(num, den);
  num /= common;
  den /= common;
  // `1 × 127/5` is 25.4; `1 × 0.0254/0.001` is not. The reduction above is what makes the
  // difference, so the multiply-then-divide order here is load-bearing rather than stylistic.
  return (value * Number(num)) / Number(den);
}

/** The converter's own state, kept beside the calculator's so a mode switch loses nothing. */
export interface ConvertState {
  category: ConvertCategory;
  from: string;
  to: string;
}

export function initialConvert(): ConvertState {
  // Length, m → ft: the first category, and a conversion whose answer a person can sanity-check.
  return { category: 'length', from: 'm', to: 'ft' };
}

/**
 * Move to another category, keeping the two dropdowns valid.
 *
 * A category change cannot preserve the units — `kg` is not a length — so it takes the new
 * category's base and its second unit, which is the same shape the initial state has.
 */
export function setConvertCategory(state: ConvertState, category: ConvertCategory): ConvertState {
  const units = CONVERT_UNITS[category];
  void state;
  return {
    category,
    from: units[0]?.label ?? '',
    to: (units[1] ?? units[0])?.label ?? '',
  };
}
